import * as React from 'react';
import {
  StackItem,
  StackLayout,
  StatusBadge,
  UtilityText,
} from '@leagueplatform/genesis-core';
import {
  HealthMetricDataV3,
  aggregateIndicators,
} from '#components/charts/core/range-graph/utils/use-health-metric-values.ts';
import { DatumLegend } from '#pages/details2/components/datum-legend.tsx';
import { shapes } from '#components/charts/core/xychart/primitives/Shape.tsx';
import { useFormatDateByUserProfileLocation } from '@leagueplatform/web-common';
import { getHealthMetricDisplayValue } from '#utils/get-health-metric-display-value.ts';
import { getHealthMetricUnitDisplayValue } from '#utils/get-health-metric-unit-display-value.ts';
import {
  MasonryChartAggregationBucketType,
  MasonryChartInterval,
} from '#types/masonry-chart.type.ts';
import { ChartTooltip } from '../chart-tooltip';
import {
  bucketDateFormatMap,
  intervalDateFormatMap,
} from './utils/interval-format-map';

type MultipleMeasurementsTooltipProps = {
  data: HealthMetricDataV3;
  interval: MasonryChartInterval;
  aggregationBucketType?: MasonryChartAggregationBucketType;
};

export function MultipleMeasurementsTooltipV3({
  data,
  interval,
  aggregationBucketType,
}: MultipleMeasurementsTooltipProps) {
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();

  const indicator = React.useMemo(() => aggregateIndicators(data), [data]);

  const dateFormat = aggregationBucketType
    ? bucketDateFormatMap[aggregationBucketType]
    : intervalDateFormatMap[interval];

  return (
    <ChartTooltip>
      <StackLayout spacing="$half">
        {data.map((datum) => {
          const shape = shapes[datum.metric.data.order];
          const value = getHealthMetricDisplayValue(datum);
          const unit = getHealthMetricUnitDisplayValue(
            value,
            datum.metric.data.unit,
          );

          return (
            <StackItem key={datum.metric.data.order}>
              <DatumLegend
                size="small"
                shape={data.length > 1 ? shape : undefined}
                name={datum.metric.data.name.toUpperCase()}
                value={value}
                unit={unit}
              />
            </StackItem>
          );
        })}
        {indicator ? (
          <StatusBadge
            showLight={false}
            label={indicator.label}
            status={indicator.style}
          />
        ) : null}
        <UtilityText size="eyebrow">
          {formatDateWithUserProfileLocation(data[0].timestamp, dateFormat)}
        </UtilityText>
      </StackLayout>
    </ChartTooltip>
  );
}
